/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import moment from 'moment';

// Material components
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
// import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import TimePicker from '@mui/lab/TimePicker';
import DesktopDatePicker from '@mui/lab/DesktopDatePicker';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import TablePagination from '@mui/material/TablePagination';

// Components
import Modal from '../../components/Modal';
import ModalLoading from '../../components/ModalLoading';

// Resources
import { ISurvey } from '../../interfaces/survey';
import { IAlert } from '../../interfaces/alert';
import { useGroup } from '../../hooks/useGroup';
import { useShift } from '../../hooks/useShift';
import { useHole } from '../../hooks/useHole';
import { createReport } from '../../api/reports';
import { getSurveyReports, surveyAPI } from '../../api/survey';

// Assets
import { useStyles } from './styles';
import { config } from '../../config';
import { downloadImage } from '../../utils';

const SurveyReport = () => {
	const [survey, setSurvey] = useState<ISurvey>({} as ISurvey);
	const [modalForm, setModalForm] = useState(false);
	const [modalReport, setModalReport] = useState(false);
	const [loading, setLoading] = useState(false);
	const [report, setReport] = useState('');
	const [surveyArr, setSurveyArr] = useState<ISurvey[]>([]);
	const [alert, setAlert] = useState<IAlert>({
		visible: false,
		message: '',
		severity: 'success',
	});
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(20);
	const { groups, group, setGroup } = useGroup();
	const { shifts, shift, setShift } = useShift();
	const { holes, hole, setHole } = useHole();
	const classes = useStyles();

	useEffect(() => {
		_getDataFromApi();
	}, []);

	useEffect(() => {
		const survAngle = parseFloat(survey.angle) || 0;
		const deviationAngle =
			Math.trunc((survAngle - parseFloat(hole.angle)) * 100) / 100;
		setSurvey({
			...survey,
			deviation_angle: survAngle ? deviationAngle.toString() : hole.angle,
		});
	}, [survey.angle]);

	useEffect(() => {
		const northAzimuth =
			Math.trunc(
				((parseFloat(survey.azimuth) || 0) +
					config.staticValues.margentic_declination) *
					100,
			) / 100;
		const deviationAzimuth = (
			Math.trunc((northAzimuth - parseFloat(hole.azimuth)) * 100) / 100
		).toString();
		setSurvey({
			...survey,
			northern_azimuth: survey.azimuth ? northAzimuth.toString() : '',
			deviation_azimuth: survey.azimuth ? deviationAzimuth : '',
		});
	}, [survey.azimuth]);

	const _getDataFromApi = (): void => {
		getSurveyReports({
			includes: [
				{
					model: 'group',
					attributes: ['id', 'name', 'image'],
				},
				{
					model: 'hole',
				},
			],
		}).then(res => {
			if (Array.isArray(res)) {
				setSurveyArr(res.reverse() || []);
			}
		});
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const onPrintReport = (item: ISurvey) => {
		const parseData: ISurvey = {
			...item,
			time: moment(item.time).format('HH:mm'),
			date: moment(item.date).format('MM/DD/YYYY'),
		};

		const payload = {
			idReport: 54,
			format: 'image',
			payload: {
				Report: { ...parseData },
				Options: { Language: 'es' },
			},
		};

		setLoading(true);
		createReport<ISurvey>(payload)
			.then(({ data }) => {
				setReport(
					`data:application/pdf;headers=filename%3D${
						item.date || 'download_file'
					};base64,${escape(data)}` || '',
				);
				setModalReport(true);
			})
			.finally(() => setLoading(false));
	};

	const _handleSaveSurvey = (): void => {
		const surveyFlag: ISurvey = {
			...survey,
			shift,
			time: survey.time
				? moment(survey.time).format('MM/DD/YYYY HH:mm:ss')
				: moment().format('MM/DD/YYYY HH:mm:ss'),
			date: survey.date || moment().format('MM/DD/YYYY'),
			group_id: group.id,
			hole_id: hole.id,
		};

		setLoading(true);
		surveyAPI
			.post('', surveyFlag)
			.then(res => {
				if (res) {
					setAlert({
						visible: true,
						severity: 'success',
						message: 'The survey report has been saved successfully',
					});
					setModalForm(false);
					setSurvey({} as ISurvey);
					_getDataFromApi();
				}
			})
			.catch(() => {
				setAlert({
					visible: true,
					severity: 'error',
					message: 'Error, the survey report has not been saved',
				});
			})
			.finally(() => setLoading(false));
	};

	const _handleCleanData = (): void => {
		setModalForm(false);
		setSurvey({} as ISurvey);
	};

	return (
		<div className={classes.root}>
			<div className={classes.containerButtonAdd}>
				<FormControl fullWidth className={classes.padding5}>
					<InputLabel id="group">Groups</InputLabel>
					<Select
						labelId="group"
						id="group"
						value={group.id || ''}
						label="Group"
						onChange={e => setGroup(e.target.value)}
					>
						{(groups || []).map((item, index) => (
							<MenuItem key={index} value={item.id}>
								{item.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl fullWidth className={classes.padding5}>
					<InputLabel id="shift">Shifts</InputLabel>
					<Select
						labelId="shift"
						id="shift"
						value={shift || ''}
						label="Shift"
						onChange={e => setShift(e.target.value)}
					>
						{(shifts || []).map((item, index) => (
							<MenuItem key={index} value={item}>
								{item}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl fullWidth className={classes.padding5}>
					<InputLabel id="hole">Holes</InputLabel>
					<Select
						labelId="hole"
						id="hole"
						value={hole.id || ''}
						label="Hole"
						onChange={e => setHole(e.target.value)}
					>
						{(holes || []).map((item, index) => (
							<MenuItem key={index} value={item.id}>
								{item.hole_number}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</div>
			<div className={classes.containerButtonAdd}>
				<Button
					className={classes.btnAdd}
					variant="contained"
					onClick={() => setModalForm(true)}
				>
					Add Survey
				</Button>
			</div>
			<TableContainer component={Paper}>
				<Table sx={{ minWidth: 650 }} aria-label="simple table">
					<TableHead className={classes.headerTable}>
						<TableRow>
							<TableCell className={classes.headerTextTable}>Group</TableCell>
							<TableCell className={classes.headerTextTable}>Shift</TableCell>
							<TableCell className={classes.headerTextTable}>Time</TableCell>
							<TableCell className={classes.headerTextTable}>Date</TableCell>
							<TableCell
								size="small"
								className={classes.headerTextTableActions}
							>
								Actions
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{(surveyArr || [])
							.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
							.map(row => (
								<TableRow
									key={row.id}
									sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
								>
									<TableCell align="left">{row.group?.name}</TableCell>
									<TableCell align="left">{row.shift}</TableCell>
									<TableCell align="left">
										{moment(row.time).format('HH:mm')}
									</TableCell>
									<TableCell align="left">
										{moment(row.date).format('MM/DD/YYYY')}
									</TableCell>
									<TableCell align="left">
										<IconButton
											size="large"
											edge="start"
											color="inherit"
											aria-label="delete"
											sx={{ mr: 2 }}
											onClick={() => onPrintReport(row)}
										>
											<PrintIcon />
										</IconButton>
										{/* <IconButton
										size="large"
										edge="start"
										color="inherit"
										aria-label="delete"
										sx={{ mr: 2 }}
									>
										<DeleteIcon />
									</IconButton> */}
									</TableCell>
								</TableRow>
							))}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				rowsPerPageOptions={[20, 50, 100]}
				component="div"
				count={surveyArr.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
			<Modal
				onView={modalForm}
				title="Survey Form"
				onClose={_handleCleanData}
				onSave={_handleSaveSurvey}
			>
				<Grid container rowSpacing={3} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
					<Grid item xs={4}>
						<Typography variant="h6">Group</Typography>
						<Typography variant="subtitle1" color="primary">
							{group?.name}
						</Typography>
					</Grid>
					<Grid item xs={4}>
						<Typography variant="h6">Shift</Typography>
						<Typography variant="subtitle1" color="primary">
							{shift}
						</Typography>
					</Grid>
					<Grid item xs={4}>
						<Typography variant="h6">Hole Number</Typography>
						<Typography variant="subtitle1" color="primary">
							{hole?.hole_number}
						</Typography>
					</Grid>
					<Grid item xs={6}>
						<DesktopDatePicker
							label="Date"
							inputFormat="MM/DD/YYYY"
							value={survey.date || new Date()}
							onChange={e =>
								setSurvey({ ...survey, date: moment(e).format('MM/DD/YYYY') })
							}
							renderInput={(params: any) => (
								<TextField fullWidth variant="standard" {...params} />
							)}
						/>
					</Grid>
					<Grid item xs={6}>
						<TimePicker
							label="Time"
							value={survey.time || new Date()}
							onChange={e => setSurvey({ ...survey, time: e })}
							renderInput={(params: any) => (
								<TextField fullWidth variant="standard" {...params} />
							)}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							id="depth"
							label="Depth"
							className={classes.textField}
							variant="standard"
							fullWidth
							value={survey.depth || ''}
							onChange={e => setSurvey({ ...survey, depth: e.target.value })}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							id="azimuth"
							label="Azimuth"
							className={classes.textField}
							variant="standard"
							fullWidth
							value={survey.azimuth || ''}
							onChange={e => setSurvey({ ...survey, azimuth: e.target.value })}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							id="northern_azimuth"
							label="Northern Azimuth"
							className={classes.textField}
							variant="standard"
							fullWidth
							value={survey.northern_azimuth || ''}
							disabled
							onChange={e =>
								setSurvey({ ...survey, northern_azimuth: e.target.value })
							}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							id="angle"
							label="Angle"
							className={classes.textField}
							variant="standard"
							fullWidth
							value={survey.angle || ''}
							onChange={e => setSurvey({ ...survey, angle: e.target.value })}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							id="magnetic_inclination"
							label="Magnetic Inclination"
							className={classes.textField}
							variant="standard"
							fullWidth
							value={survey.magnetic_inclination || ''}
							onChange={e =>
								setSurvey({ ...survey, magnetic_inclination: e.target.value })
							}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							id="gravity"
							label="Gravity"
							className={classes.textField}
							variant="standard"
							fullWidth
							value={survey.gravity || ''}
							onChange={e => setSurvey({ ...survey, gravity: e.target.value })}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							id="temperature"
							label="Temperature"
							className={classes.textField}
							variant="standard"
							fullWidth
							value={survey.temperature || ''}
							onChange={e =>
								setSurvey({ ...survey, temperature: e.target.value })
							}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							id="magnetic_field"
							label="Magnetic Field"
							className={classes.textField}
							variant="standard"
							fullWidth
							value={survey.magnetic_field || ''}
							onChange={e =>
								setSurvey({ ...survey, magnetic_field: e.target.value })
							}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							id="deviation_azimuth"
							label="Deviation Azimuth"
							className={classes.textField}
							variant="standard"
							fullWidth
							disabled
							value={survey.deviation_azimuth || ''}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							id="deviation_angle"
							label="Deviation Angle"
							className={classes.textField}
							variant="standard"
							fullWidth
							disabled
							value={survey.deviation_angle || ''}
						/>
					</Grid>
				</Grid>
			</Modal>
			<Modal
				onView={modalReport}
				title="Survey Report"
				width="lg"
				textCancel="Close"
				onClose={() => setModalReport(false)}
				onDownloadReport={() =>
					downloadImage({ payload: report, name: 'Survey Report' })
				}
			>
				<div>
					<img src={report} width="auto" height="auto" />
				</div>
			</Modal>
			<ModalLoading onView={loading} />
			<Snackbar
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				open={alert.visible}
				onClose={() => setAlert({ ...alert, visible: false })}
			>
				<MuiAlert
					elevation={6}
					severity={alert.severity}
					variant="filled"
					onClose={() => setAlert({ ...alert, visible: false })}
					sx={{ width: '100%' }}
				>
					{alert.message}
				</MuiAlert>
			</Snackbar>
		</div>
	);
};

export default SurveyReport;
